import React, { useState, useEffect } from 'react';
import { useCart } from '../../context/CartContext';
import { calculateBoxWeight, calculateTotal } from '../../toolkit/shipping';
import { formatCustomizationForDisplay } from '../../utils/customizationUtils';
import './styles.css';

// Helper function to generate UUID without external dependencies
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};


const Checkout: React.FC = () => {
  const { 
    cart, 
    clearCart, 
    customerInfo, 
    shippingAddress,
    setCustomerInfo, 
    setShippingAddress,
    setOrderInfo: setOrderInfo,
    orderInfo 
  } = useCart();
  
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [isPickup, setIsPickup] = useState(false);
  const [freeShipping, setFreeShipping] = useState(false);
  const [amount_left_for_free_shipping, setAmountLeftForFreeShipping] = useState(0);

  // Pre-fill form with existing customer info if available
  useEffect(() => {
    if (customerInfo) {
      setName(customerInfo.name);
      setEmail(customerInfo.email);
    }
    if (shippingAddress) {
      setAddressLine1(shippingAddress.addressLine1);
      setAddressLine2(shippingAddress.addressLine2);
      setCity(shippingAddress.city);
      setState(shippingAddress.state);
      setZipCode(shippingAddress.zipCode);
    }
  }, [customerInfo, shippingAddress]);

  // Prevent caching of checkout page
  useEffect(() => {
    // Clear any existing form data from previous visits
    setEmail('');
    setName('');
    setAddressLine1('');
    setAddressLine2('');
    setCity('');
    setState('');
    setZipCode('');
    
    // Clear any form elements that might have cached values
    const form = document.querySelector('form');
    if (form) {
      form.reset();
    }
    
    // Add cache-busting timestamp to prevent browser caching
    const timestamp = Date.now();
    const url = new URL(window.location.href);
    if (!url.searchParams.has('_t')) {
      url.searchParams.set('_t', timestamp.toString());
      window.history.replaceState({}, '', url.toString());
    }
    
    // Force browser to not use cached version
    if ('caches' in window) {
      caches.keys().then(names => {
        names.forEach(name => {
          caches.delete(name);
        });
      });
    }
  }, []);

  useEffect(() => {
    if (cart.length > 0 && cart.reduce((sum, item) => sum + item.price * item.quantity, 0) >= 100) {
      setFreeShipping(true);
      setAmountLeftForFreeShipping(100 - cart.reduce((sum, item) => sum + item.price * item.quantity, 0));
    } else {
      setFreeShipping(false);
    }
  }, [cart]);

  const total = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const content_weight_grams = cart.reduce((sum, item) => sum + item.weight_grams * item.quantity, 0);
  const total_items = cart.reduce((sum, item) => sum + item.quantity, 0);
  const { tax: total_tax, shipping: base_shipping, total: base_total } = calculateTotal(total, total_items, zipCode, state, isPickup, content_weight_grams);
  const shipping_cost = isPickup ? 0 : base_shipping;
  const grand_total = isPickup ? (total + total_tax) : base_total;

  const hogcloud_payment_handler_url = process.env.REACT_APP_HOGCLOUD_PAYMENT_HANDLER_URL || "";

  const box_weight_grams = calculateBoxWeight(total_items);

  const total_weight_grams = content_weight_grams + box_weight_grams;

  //round to 2 decimal places
  const total_weight_oz = Math.round(total_weight_grams / 28.3495 * 100) / 100;
  const handlePay = async () => {
    if (!hogcloud_payment_handler_url) {
      alert('Payment handler URL is not configured. Please contact support.');
      return;
    }
    
    if (!email || !name) {
      alert('Please enter your name and email.');
      return;
    }
    
    if (!isPickup && (!addressLine1 || !city || !state || !zipCode)) {
      alert('Please enter your complete shipping address.');
      return;
    }
    
    setLoading(true);
        
    try {
      // Generate order ID
      const customerData = { name, email };
      
      // Save to session
      setCustomerInfo(customerData);
      if (!isPickup) {
        const shippingData = {
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode
        };
        setShippingAddress(shippingData);
      }
      setOrderInfo({
        isPickup: isPickup,
        checkout_request_id: generateUUID(),
        createdAt: new Date()
      });

      // Get API key from environment variable with fallback for development
      const PAYMENT_HANDLER_API_KEY = process.env.REACT_APP_PAYMENT_HANDLER_API_KEY || "";
      const x_api_key = btoa(PAYMENT_HANDLER_API_KEY);
      const hogcloud_payment_handler_url = process.env.REACT_APP_HOGCLOUD_PAYMENT_HANDLER_URL || "";
      
      // Get the checkout_request_id from the order info
      const checkout_request_id = orderInfo?.checkout_request_id || generateUUID();
      
      // Build shipping address object only for shipping orders with valid data
      let shippingAddressData = null;
      if (!isPickup && addressLine1 && city && state && zipCode) {
        shippingAddressData = {
          addressLine1,
          addressLine2,
          city,
          state,
          zipCode
        };
      }
      
      const response = await fetch(hogcloud_payment_handler_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': x_api_key
        },
        body: JSON.stringify({
          items: cart,
          email: email,
          name: name,
          shippingAddress: shippingAddressData,
          taxAmount: total_tax,
          shippingAmount: shipping_cost,
          totalAmount: grand_total,
          totalWeight: total_weight_grams,
          isPickup: isPickup,
          checkout_request_id: checkout_request_id
        })
      });

      // Check if response is ok before trying to parse JSON
      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        
        try {
          // Try to get error details from response body
          const errorText = await response.text();
          if (errorText) {
            // If it's JSON, try to parse it
            try {
              const errorData = JSON.parse(errorText);
              errorMessage = errorData.message || errorData.error || errorMessage;
            } catch {
              // If not JSON, use the text as is (truncated if too long)
              errorMessage = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText;
            }
          }
        } catch (textError) {
          console.warn('Could not read error response body:', textError);
        }
        
        throw new Error(`Payment handler error: ${errorMessage}`);
      }

      const data = await response.json();
      
      if (data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error(data.message || 'Payment initiation failed - no checkout URL received');
      }
    } catch (error: unknown) {
      console.error('Payment error:', error);
      
      // Show more helpful error message
      let errorMessage = 'Payment initiation failed. Please try again.';
      
      if (error instanceof Error && error.message) {
        if (error.message.includes('502')) {
          errorMessage = 'Payment service is temporarily unavailable. Please try again in a few minutes.';
        } else if (error.message.includes('Payment handler error:')) {
          errorMessage = error.message.replace('Payment handler error: ', '');
        } else if (error.message.includes('Failed to fetch')) {
          errorMessage = 'Unable to connect to payment service. Please check your internet connection.';
        }
      }
      
      alert(errorMessage);
    }
    setLoading(false);
  };

  return (
    <div className="invoice-container">

      {cart.length === 0 ? (
        <p>Your cart is empty.</p>
      ) : (
        <div className="invoice-content">
          <div className="invoice-details">
            <div className="customer-info">
              <h3>Bill To:</h3>
              <div className="form-group">
                <label>Name:</label>
                <input 
                  value={name} 
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter your name"
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input 
                  type="email" 
                  value={email} 
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Enter your email"
                />
                <br/>
                <b>Please ensure your email address is correct before submitting your order*</b>
              </div>

            </div>

            <div className="pickup-option">
              <div className="form-group">
                <label className="checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={isPickup} 
                    onChange={e => setIsPickup(e.target.checked)}
                  />
                  Pickup in Fayetteville
                </label>
              </div>
            </div>

            {isPickup && (
              <div className="pickup-info">
                <h3>Pickup at Fayetteville Public Library</h3>
                <p><b>South Parking Lot</b><br/>Wednesday and Fridays<br/>12:00 PM - 1:00 PM</p>
              </div>
            )}

            {!isPickup && (<div className="shipping-info">
              <div className="shipping-info-note">
                <p><strong>📦 Packaging:</strong> All items are carefully packaged in either bubble mailers or boxes. <a href="/shipping" className="shipping-link">More info</a></p>
                <div className="summary-row">
                  <span>Packaging Weight: {total_weight_oz}oz [ Contents: {content_weight_grams}g, Packaging: {box_weight_grams}g]</span>
                </div>
              </div>
              <h3>Shipping Address:</h3>

              <div className="form-group">
                <label>Address Line 1:</label>
                <input 
                  value={addressLine1} 
                  onChange={e => setAddressLine1(e.target.value)}
                  placeholder="Street address"
                  disabled={isPickup}
                />
              </div>
              <div className="form-group">
                <label>Address Line 2:</label>
                <input 
                  value={addressLine2} 
                  onChange={e => setAddressLine2(e.target.value)}
                  placeholder="Apt, suite, etc. (optional)"
                  disabled={isPickup}
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City:</label>
                  <input 
                    value={city} 
                    onChange={e => setCity(e.target.value)}
                    placeholder="City"
                    disabled={isPickup}
                  />
                </div>
                <div className="form-group">
                  <label>State:</label>
                  <input 
                    value={state} 
                    onChange={e => setState(e.target.value)}
                    placeholder="State"
                    disabled={isPickup}
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code:</label>
                  <input 
                    value={zipCode} 
                    onChange={e => setZipCode(e.target.value)}
                    placeholder="Zip code"
                    disabled={isPickup}
                  />
                </div>
              </div>
            </div>
            )}
          </div>

          <div className="invoice-items">
            <table>
              <thead>
                <tr>
                  <th>Item</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Total</th>
                </tr>
              </thead>
              <tbody>
                {cart.map(item => (
                  <tr key={item.id}>
                    <td>
                      {item.name}
                      {item.is_customizeable && item.customization_items && (
                        <div className="customization-details">
                          ({formatCustomizationForDisplay(item.customization_items)})
                        </div>
                      )}
                    </td>
                    <td>{item.quantity}</td>
                    <td>${item.price.toFixed(2)}</td>
                    <td>${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
                {!freeShipping && (
                  <div className="invoice-summary">
                    <h3>Free shipping on purchases over $100!</h3>
                  </div>
                )}
          <div className="invoice-summary">
            <div className="summary-row">
              <span>Subtotal:</span>
              <span>${total.toFixed(2)}</span>
            </div>
            <div className="summary-row">
              <span>Tax:</span>
              <span>${total_tax.toFixed(2)}</span>
            </div>
            {freeShipping ? (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>Free! You've earned it.</span>
              </div>
            ) : (
              <div className="summary-row">
                <span>Shipping:</span>
                <span>${shipping_cost.toFixed(2)}</span>
              </div>
            )}
            <div className="summary-row total">
              <span>Total:</span>
              <span>${grand_total.toFixed(2)}</span>
            </div>
          </div>

          <div className="invoice-actions">
            <button 
              className="continue-shopping-button" 
              onClick={() => window.history.back()}
            >
              Shop
            </button>
            <button 
              className="pay-button" 
              onClick={handlePay} 
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Pay Now'}
            </button>
          </div>
          <div className="invoice-footer">
            <p><i>You will be added to our mailing list. You can unsubscribe at any time</i></p>
          </div>
        </div>

      )}
    </div>
  );
};

export default Checkout;

